import Quill from "quill";
import type Image from "quill/formats/image";
import {ImagePreviewHelper} from "../../helpers/image-preview.helper";

// Quill's `import(name: string)` overload is typed `unknown`; the 'formats/image' key is
// registered by Quill itself with the class that `quill/formats/image` exports.
const ImageFormatter = Quill.import('formats/image') as typeof Image;

/**
 * Quill's image, opening the enlarged preview on click. Since the switch to tiptap
 * (2026-09-14) the only Quill left is the read-only `ha-quill-viewer` for legacy
 * descriptions, so this now runs only in read views — where the preview belongs
 * (Anke, 2026-09-26). It used to skip exactly that viewer (`isInReadOnlyMode`),
 * which left the preview with no path at all; that check is gone.
 *
 * Same preview and same rules as tiptap's view mode (`ImagePreviewListener`): an
 * image inside a link (the external link tile) follows the link; otherwise the
 * click is cancelled and stopped, so a clickable task card around the text does not
 * open as well.
 */
export class ImageBlot extends ImageFormatter {

    static create(value: string) {
        const node = super.create(value) as HTMLImageElement;
        if (typeof value === 'string') {
            node.setAttribute('src', this.sanitize(value));
        }

        node.addEventListener('click', (evt: MouseEvent) => {
            if (ImagePreviewHelper.isInLink(evt.composedPath())) {
                return;
            }

            evt.preventDefault();
            evt.stopPropagation();
            // The viewer, not the image, owns the preview: `ha-quill-viewer` rebuilds
            // every image on each new value (`setContents`), so a live refresh of the
            // list would otherwise close the preview under the user's eyes. The viewer
            // itself only leaves with its view — the same as tiptap's host element.
            ImagePreviewHelper.open(node.src, node.alt, node.closest('ha-quill-viewer') ?? node);
        });

        return node;
    }

}

Quill.register("formats/image", ImageBlot, true);